/**
 * Two-finger pinch-zoom and drag (§9.2 `pinch`). Like `pan` this is a
 * viewport gesture handled ahead of the active tool, and like `pan` it works
 * in screen pixels. A pinch never edits the document, so it emits no
 * `beginGesture`.
 */
import { zoomAbout, MAX_ZOOM, MIN_ZOOM } from './pan'
import type { ToolEffect, ToolEvent, ToolMachine, ToolResult } from './types'
import { none, withState } from './types'

export interface PinchContext {
  viewport: { k: number; tx: number; ty: number }
}

const centreOf = (pts: Map<number, { x: number; y: number }>): [number, number] => {
  let sx = 0
  let sy = 0
  for (const p of pts.values()) {
    sx += p.x
    sy += p.y
  }
  const n = Math.max(1, pts.size)
  return [sx / n, sy / n]
}

const spreadOf = (pts: Map<number, { x: number; y: number }>): number => {
  const list = [...pts.values()]
  const a = list[0]
  const b = list[1]
  if (!a || !b) return 0
  return Math.hypot(a.x - b.x, a.y - b.y)
}

/**
 * Feed every touch pointer here. Returns a result while two or more pointers
 * are down, `null` otherwise so the active tool keeps single-finger events.
 */
export function reducePinch(machine: ToolMachine, ev: ToolEvent, ctx: PinchContext, down: Map<number, { x: number; y: number }>): ToolResult | null {
  const st = machine.state

  if (ev.t === 'pointerdown' && down.size >= 2) {
    const pointers = new Map(down)
    return withState(machine.tool, {
      t: 'pinch',
      pointers,
      startK: ctx.viewport.k,
      startDist: spreadOf(pointers) || 1,
      startCenter: centreOf(pointers),
    })
  }

  if (st.t !== 'pinch') return null

  if (ev.t === 'pointermove') {
    const pointers = new Map(down)
    if (pointers.size < 2) return none(machine)
    const dist = spreadOf(pointers)
    if (dist <= 0) return none(machine)
    const k = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, st.startK * (dist / st.startDist)))
    const [cx, cy] = centreOf(pointers)
    const next = zoomAbout(ctx.viewport, k, cx, cy)
    const effects: ToolEffect[] = [
      { k: 'zoomTo', scale: next.k, centre: { x: cx, y: cy } },
      { k: 'panTo', tx: next.tx, ty: next.ty },
    ]
    return withState(machine.tool, { ...st, pointers }, effects)
  }

  if (ev.t === 'pointerup' || ev.t === 'pointercancel' || ev.t === 'cancel') {
    if (down.size >= 2) return none(machine)
    return { machine: { tool: machine.tool, state: { t: 'idle' } }, effects: [] }
  }

  return none(machine)
}
